import React, { useState, useEffect } from 'react';
import { Palette, Image, Languages, Loader2, Download, Copy, Check, Wand2, Sparkles, Brush, Sliders } from 'lucide-react';
import { getSupportedLanguages, colorizeImage, generateCover, translateText, getArtStyles, getEnhancementOptions, styleTransfer, enhancePanel, getGenres } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

function AIToolsPage() {
  const { user, isCreator } = useAuth();
  const [activeTab, setActiveTab] = useState('colorize');
  const [languages, setLanguages] = useState([]);
  const [genres, setGenres] = useState([]);
  const [artStyles, setArtStyles] = useState([]);
  const [enhanceOptions, setEnhanceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Colorize state
  const [colorImage, setColorImage] = useState(null);
  const [colorImagePreview, setColorImagePreview] = useState(null);
  const [colorDescription, setColorDescription] = useState('');

  // Cover generation state
  const [coverTitle, setCoverTitle] = useState('');
  const [coverDescription, setCoverDescription] = useState('');
  const [coverGenre, setCoverGenre] = useState('fantasy');
  const [coverStyle, setCoverStyle] = useState('');

  // Translation state
  const [translateTextValue, setTranslateTextValue] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('en');
  const [targetLanguage, setTargetLanguage] = useState('ja');

  // Style transfer state
  const [styleImage, setStyleImage] = useState(null);
  const [styleImagePreview, setStyleImagePreview] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('ghibli');
  const [styleIntensity, setStyleIntensity] = useState('medium');

  // Enhance state
  const [enhanceImage, setEnhanceImage] = useState(null);
  const [enhanceImagePreview, setEnhanceImagePreview] = useState(null);
  const [selectedEnhancements, setSelectedEnhancements] = useState(['lineart', 'colors']);
  const [preserveStyle, setPreserveStyle] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [langRes, genreRes, stylesRes, enhanceRes] = await Promise.all([
          getSupportedLanguages(),
          getGenres(),
          getArtStyles(),
          getEnhancementOptions()
        ]);
        setLanguages(langRes.data);
        setGenres(genreRes.data);
        setArtStyles(stylesRes.data);
        setEnhanceOptions(enhanceRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
    fetchData();
  }, []);

  const handleImageUpload = (e, setter, previewSetter) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setter(base64);
        previewSetter(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorize = async () => {
    if (!colorImage || !colorDescription) {
      toast.error('Please upload an image and describe the colors');
      return;
    }
    setLoading(true);
    setLoadingMessage('AI is colorizing your image... (this may take up to 90 seconds)');
    setResult(null);
    try {
      const res = await colorizeImage(colorImage, colorDescription);
      setResult({ type: 'image', data: res.data.image_base64, mime: res.data.mime_type });
      toast.success('Image colorized!');
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Colorization failed';
      if (error.response?.status === 504) {
        toast.error('Request timed out. Please try again with a simpler description.');
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleGenerateCover = async () => {
    if (!coverTitle || !coverDescription) {
      toast.error('Please enter title and description');
      return;
    }
    setLoading(true);
    setLoadingMessage('AI is generating your cover... (this may take up to 90 seconds)');
    setResult(null);
    try {
      const res = await generateCover(coverTitle, coverDescription, coverGenre, coverStyle);
      setResult({ type: 'image', data: res.data.image_base64, mime: res.data.mime_type });
      toast.success('Cover generated!');
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Cover generation failed';
      if (error.response?.status === 504) {
        toast.error('Request timed out. Please try again with a simpler description.');
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleTranslate = async () => {
    if (!translateTextValue) {
      toast.error('Please enter text to translate');
      return;
    }
    setLoading(true);
    setLoadingMessage('AI is translating...');
    setResult(null);
    try {
      const res = await translateText(translateTextValue, sourceLanguage, targetLanguage);
      setResult({ type: 'text', data: res.data.translated_text });
      toast.success('Translation complete!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Translation failed');
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleStyleTransfer = async () => {
    if (!styleImage) {
      toast.error('Please upload an image');
      return;
    }
    setLoading(true);
    setLoadingMessage('AI is applying style transfer... (this may take up to 90 seconds)');
    setResult(null);
    try {
      const res = await styleTransfer(styleImage, selectedStyle, styleIntensity);
      setResult({ type: 'image', data: res.data.image_base64, mime: res.data.mime_type, message: res.data.message });
      toast.success(`Applied ${res.data.style_applied} style!`);
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Style transfer failed';
      if (error.response?.status === 504) {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleEnhance = async () => {
    if (!enhanceImage) {
      toast.error('Please upload an image');
      return;
    }
    if (selectedEnhancements.length === 0) {
      toast.error('Please select at least one enhancement');
      return;
    }
    setLoading(true);
    setLoadingMessage('AI is enhancing your panel... (this may take up to 90 seconds)');
    setResult(null);
    try {
      const res = await enhancePanel(enhanceImage, selectedEnhancements, preserveStyle);
      setResult({ type: 'image', data: res.data.image_base64, mime: res.data.mime_type, message: res.data.message });
      toast.success('Panel enhanced!');
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Enhancement failed';
      if (error.response?.status === 504) {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error(errMsg);
      }
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const toggleEnhancement = (id) => {
    setSelectedEnhancements(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const downloadResult = () => {
    if (result?.type === 'image') {
      const link = document.createElement('a');
      link.href = `data:${result.mime || 'image/png'};base64,${result.data}`;
      link.download = `supremics-ai-${Date.now()}.png`;
      link.click();
    }
  };

  const copyText = () => {
    if (result?.type === 'text') {
      navigator.clipboard.writeText(result.data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isCreator && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <Sparkles size={64} className="mx-auto mb-6 text-[var(--primary)]" />
          <h1 className="text-3xl font-teko mb-4">Creator Feature</h1>
          <p className="text-[var(--text-secondary)] mb-6">
            AI tools are available for Creator and Contributor tier subscribers.
          </p>
          <a href="/pricing" className="btn-hotrod inline-block">
            <span>Upgrade Now</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="ai-tools-page">
      <SEO title="AI Creator Studio" description="Powerful AI tools for comic creators" />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="page-padding py-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Wand2 className="text-[var(--primary)]" size={32} />
              <Sparkles className="text-[var(--secondary)]" size={24} />
            </div>
            <h1 className="text-4xl md:text-5xl font-teko font-semibold mb-3">AI Creator Studio</h1>
            <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
              Powerful AI tools to colorize, style, enhance, translate, and generate comic content
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-padding py-8">
        <div className="max-w-6xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full flex-wrap justify-start bg-[var(--surface)] border border-[var(--border)] rounded-xl mb-8 p-1">
              <TabsTrigger value="colorize" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">
                <Palette size={16} className="mr-2" /> Colorize
              </TabsTrigger>
              <TabsTrigger value="style" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">
                <Brush size={16} className="mr-2" /> Style Transfer
              </TabsTrigger>
              <TabsTrigger value="enhance" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">
                <Sliders size={16} className="mr-2" /> Enhance
              </TabsTrigger>
              <TabsTrigger value="cover" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">
                <Image size={16} className="mr-2" /> Generate Cover
              </TabsTrigger>
              <TabsTrigger value="translate" className="rounded-lg data-[state=active]:bg-[var(--primary)] data-[state=active]:text-white">
                <Languages size={16} className="mr-2" /> Translate
              </TabsTrigger>
            </TabsList>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Panel */}
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6">
                
                {/* Colorize Tab */}
                <TabsContent value="colorize" className="mt-0">
                  <h2 className="text-xl font-teko mb-4">Colorize Panel</h2>
                  <p className="text-sm text-[var(--text-secondary)] mb-6">
                    Upload a black & white comic panel and describe how you want it colored.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Upload Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, setColorImage, setColorImagePreview)}
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      />
                      {colorImagePreview && (
                        <img src={colorImagePreview} alt="Preview" className="mt-3 max-h-48 rounded-lg border border-[var(--border)]" />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Color Description</label>
                      <textarea
                        value={colorDescription}
                        onChange={(e) => setColorDescription(e.target.value)}
                        placeholder="e.g., Vibrant anime colors with soft shadows, warm sunset lighting, character has blue hair and green eyes..."
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg h-24 resize-none"
                      />
                    </div>
                    
                    <button onClick={handleColorize} disabled={loading} className="w-full btn-hotrod disabled:opacity-50">
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="animate-spin" size={18} />
                          {loadingMessage || 'Processing...'}
                        </span>
                      ) : (
                        <span>Colorize Image</span>
                      )}
                    </button>
                  </div>
                </TabsContent>

                {/* Style Transfer Tab */}
                <TabsContent value="style" className="mt-0">
                  <h2 className="text-xl font-teko mb-4">Style Transfer</h2>
                  <p className="text-sm text-[var(--text-secondary)] mb-6">
                    Transform your art into famous manga/anime styles.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Upload Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, setStyleImage, setStyleImagePreview)}
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      />
                      {styleImagePreview && (
                        <img src={styleImagePreview} alt="Preview" className="mt-3 max-h-48 rounded-lg border border-[var(--border)]" />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Style</label>
                      {artStyles.length === 0 ? (
                        <div className="flex items-center justify-center py-8 text-[var(--text-secondary)]">
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Loading styles...
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                          {artStyles.map((style) => (
                            <button
                              key={style.id}
                              onClick={() => setSelectedStyle(style.id)}
                              className={`p-3 text-left rounded-lg border transition-all ${
                                selectedStyle === style.id 
                                  ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                                  : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                              }`}
                            >
                              <p className="font-medium text-sm">{style.name}</p>
                              <p className="text-xs text-[var(--text-secondary)] line-clamp-2">{style.description}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Intensity</label>
                      <div className="flex gap-2">
                        {['light', 'medium', 'strong'].map((level) => (
                          <button
                            key={level}
                            onClick={() => setStyleIntensity(level)}
                            className={`flex-1 py-2 px-4 rounded-lg border capitalize ${
                              styleIntensity === level 
                                ? 'border-[var(--primary)] bg-[var(--primary)] text-white' 
                                : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <button onClick={handleStyleTransfer} disabled={loading} className="w-full btn-hotrod disabled:opacity-50">
                      {loading ? <Loader2 className="animate-spin mx-auto" /> : <span>Apply Style</span>}
                    </button>
                  </div>
                </TabsContent>

                {/* Enhance Tab */}
                <TabsContent value="enhance" className="mt-0">
                  <h2 className="text-xl font-teko mb-4">Enhance Panel</h2>
                  <p className="text-sm text-[var(--text-secondary)] mb-6">
                    Improve your comic panels with AI-powered enhancements.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Upload Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, setEnhanceImage, setEnhanceImagePreview)}
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      />
                      {enhanceImagePreview && (
                        <img src={enhanceImagePreview} alt="Preview" className="mt-3 max-h-48 rounded-lg border border-[var(--border)]" />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Enhancements</label>
                      {enhanceOptions.length === 0 ? (
                        <div className="flex items-center justify-center py-8 text-[var(--text-secondary)]">
                          <Loader2 className="animate-spin mr-2" size={16} />
                          Loading options...
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {enhanceOptions.map((opt) => (
                            <button
                              key={opt.id}
                              onClick={() => toggleEnhancement(opt.id)}
                              className={`p-3 text-left rounded-lg border transition-all ${
                                selectedEnhancements.includes(opt.id) 
                                  ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                                  : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                              }`}
                            >
                              <p className="font-medium text-sm">{opt.name}</p>
                              <p className="text-xs text-[var(--text-secondary)]">{opt.description}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <label className="flex items-center gap-3 p-3 bg-[var(--background)] rounded-lg border border-[var(--border)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preserveStyle}
                        onChange={(e) => setPreserveStyle(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <div>
                        <p className="font-medium text-sm">Preserve My Style</p>
                        <p className="text-xs text-[var(--text-secondary)]">Only improve quality, don't change the art style</p>
                      </div>
                    </label>
                    
                    <button onClick={handleEnhance} disabled={loading} className="w-full btn-hotrod disabled:opacity-50">
                      {loading ? <Loader2 className="animate-spin mx-auto" /> : <span>Enhance Panel</span>}
                    </button>
                  </div>
                </TabsContent>

                {/* Cover Generation Tab */}
                <TabsContent value="cover" className="mt-0">
                  <h2 className="text-xl font-teko mb-4">Generate Cover</h2>
                  <p className="text-sm text-[var(--text-secondary)] mb-6">
                    Create a professional comic cover with AI.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Comic Title</label>
                      <input
                        type="text"
                        value={coverTitle}
                        onChange={(e) => setCoverTitle(e.target.value)}
                        placeholder="Enter your comic title"
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Genre</label>
                      <select
                        value={coverGenre}
                        onChange={(e) => setCoverGenre(e.target.value)}
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      >
                        {genres.map((g) => (
                          <option key={g.id || g} value={g.id || g}>{g.name || g}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Story Description</label>
                      <textarea
                        value={coverDescription}
                        onChange={(e) => setCoverDescription(e.target.value)}
                        placeholder="Describe your story and main characters..."
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg h-24 resize-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Style Notes (Optional)</label>
                      <input
                        type="text"
                        value={coverStyle}
                        onChange={(e) => setCoverStyle(e.target.value)}
                        placeholder="e.g., Dark fantasy, vibrant shonen, cute shoujo..."
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                      />
                    </div>
                    
                    <button onClick={handleGenerateCover} disabled={loading} className="w-full btn-hotrod disabled:opacity-50">
                      {loading ? <Loader2 className="animate-spin mx-auto" /> : <span>Generate Cover</span>}
                    </button>
                  </div>
                </TabsContent>

                {/* Translation Tab */}
                <TabsContent value="translate" className="mt-0">
                  <h2 className="text-xl font-teko mb-4">Translate Text</h2>
                  <p className="text-sm text-[var(--text-secondary)] mb-6">
                    Translate your comic dialogue while preserving tone and emotion.
                  </p>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">From</label>
                        <select
                          value={sourceLanguage}
                          onChange={(e) => setSourceLanguage(e.target.value)}
                          className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                        >
                          {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">To</label>
                        <select
                          value={targetLanguage}
                          onChange={(e) => setTargetLanguage(e.target.value)}
                          className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg"
                        >
                          {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Text to Translate</label>
                      <textarea
                        value={translateTextValue}
                        onChange={(e) => setTranslateTextValue(e.target.value)}
                        placeholder="Enter comic dialogue, speech bubbles, or narration..."
                        className="w-full p-3 bg-[var(--background)] border border-[var(--border)] rounded-lg h-32 resize-none"
                      />
                    </div>
                    
                    <button onClick={handleTranslate} disabled={loading} className="w-full btn-hotrod disabled:opacity-50">
                      {loading ? <Loader2 className="animate-spin mx-auto" /> : <span>Translate</span>}
                    </button>
                  </div>
                </TabsContent>
              </div>

              {/* Result Panel */}
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6">
                <h2 className="text-xl font-teko mb-4">Result</h2>
                
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <Loader2 size={48} className="animate-spin text-[var(--primary)] mb-4" />
                    <p className="text-[var(--text-secondary)]">AI is working its magic...</p>
                  </div>
                ) : result ? (
                  <div className="space-y-4">
                    {result.type === 'image' ? (
                      <>
                        <img 
                          src={`data:${result.mime || 'image/png'};base64,${result.data}`} 
                          alt="AI Result" 
                          className="w-full rounded-lg border border-[var(--border)]"
                        />
                        {result.message && (
                          <p className="text-sm text-[var(--text-secondary)]">{result.message}</p>
                        )}
                        <button onClick={downloadResult} className="w-full flex items-center justify-center gap-2 p-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors">
                          <Download size={18} />
                          Download Image
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="p-4 bg-[var(--background)] rounded-lg border border-[var(--border)] min-h-32">
                          <p className="whitespace-pre-wrap">{result.data}</p>
                        </div>
                        <button onClick={copyText} className="w-full flex items-center justify-center gap-2 p-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/90 transition-colors">
                          {copied ? <Check size={18} /> : <Copy size={18} />}
                          {copied ? 'Copied!' : 'Copy Text'}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-[var(--text-secondary)]">
                    <Sparkles size={48} className="mb-4 opacity-50" />
                    <p>Your AI-generated result will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default AIToolsPage;
